import React, { useEffect, useState } from 'react';
import PolicyNotice from './PolicyNotice';
import useProductStore from '../stores/productStore';
import useWidgetStore from '../stores/widgetStore';

const Footer = () => {
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPolicyOpen, setIsPolicyOpen] = useState(false);
  const { productBranding: pb } = useProductStore();
  const { applicationDataId, modalOpen, unifiedState, footerOverride } =
    useWidgetStore();
  const productBranding = unifiedState?.productBranding || pb;

  const handleTermsOpen = () => setIsTermsOpen(true);
  const handleTermsClose = () => setIsTermsOpen(false);
  const handlePolicyOpen = () => setIsPolicyOpen(true);
  const handlePolicyClose = () => setIsPolicyOpen(false);

  const hasPrivacyNotice = Boolean(productBranding?.policyNotice);
  const hasTermsAndConditions = Boolean(productBranding?.termsAndConditions);
  const hasDocumentation = Boolean(productBranding?.documentation);
  const hasContactInfo = Boolean(productBranding?.contactInfo);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const throwError = new URLSearchParams(document.location.search).get(
        'throwError',
      );
      if (throwError === 'true') {
        throw new Error('Test error');
      }
    }
  }, []);

  const renderBrandingSection = () => {
    if (!productBranding || (hasContactInfo && modalOpen)) return null;

    return (
      <div className="flex gap-2">
        <span className="text-gray-600 text-sm italic">Powered by</span>
        <div className="flex items-center gap-x-1">
          {productBranding?.icon && (
            <img
              src={productBranding.icon}
              alt="logo"
              className="max-w-[2rem]"
            />
          )}
          {productBranding.name && (
            <span className="text-primary text-sm capitalize font-extrabold italic">
              {typeof productBranding.name === 'string'
                ? productBranding.name.replace(
                    '${type}',
                    applicationDataId ? applicationDataId : '',
                  )
                : productBranding.name}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderLegalLinks = () => {
    if (
      (!hasPrivacyNotice && !hasTermsAndConditions && !hasDocumentation) ||
      (hasContactInfo && modalOpen)
    ) {
      return null;
    }

    return (
      <div className="flex items-center gap-x-2">
        {hasPrivacyNotice && (
          <a
            href="#"
            className="text-md text-primary font-semibold"
            onClick={handlePolicyOpen}
          >
            Privacy Notice
          </a>
        )}

        {hasPrivacyNotice && hasTermsAndConditions && (
          <span className="text-gray-400">|</span>
        )}

        {hasTermsAndConditions && (
          <a
            href="#"
            className="text-md text-primary font-semibold"
            onClick={handleTermsOpen}
          >
            Terms & Conditions
          </a>
        )}

        {hasDocumentation && (
          <a
            href={productBranding?.documentation}
            className="text-md text-[#9E0085] font-semibold"
            target="_blank"
          >
            User Guide
          </a>
        )}
      </div>
    );
  };

  const renderContactSection = () => {
    const contactInfo = footerOverride
      ? productBranding?.otherContactInfo
      : productBranding?.contactInfo;

    if (!contactInfo || !modalOpen) return null;

    const { header, email, phone, hours } = contactInfo;

    return (
      <div className="flex flex-col items-center text-center w-full">
        {header && (
          <div className="text-gray-900 font-bold text-sm mb-1">{header}</div>
        )}

        <div className="flex flex-wrap items-center justify-center gap-x-2 text-sm text-gray-700">
          {email && (
            <a
              href={`mailto:${email}`}
              className="text-primary hover:underline font-medium"
            >
              {email}
            </a>
          )}

          {email && phone && <span className="text-gray-400">|</span>}

          {phone && (
            <a
              href={`tel:${phone.replace(/[^0-9+]/g, '')}`}
              className="font-medium hover:text-primary whitespace-nowrap"
            >
              {phone}
            </a>
          )}

          {hours && <span className="text-xs text-gray-500 ">{hours}</span>}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center py-3 gap-1 px-6">
        {renderBrandingSection()}
        {renderContactSection()}
        {renderLegalLinks()}
      </div>

      {isPolicyOpen && (
        <PolicyNotice
          title="Privacy Notice"
          content={productBranding?.policyNotice}
          onClose={handlePolicyClose}
        />
      )}

      {isTermsOpen && (
        <PolicyNotice
          title="Terms & Conditions"
          content={productBranding?.termsAndConditions}
          onClose={handleTermsClose}
        />
      )}
    </>
  );
};

export default Footer;
